import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import './EmployeeExport.css';

const EmployeeExport = () => {
    const [loading, setLoading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [units, setUnits] = useState([]);
    const [customFields, setCustomFields] = useState([]);
    const [employeesData, setEmployeesData] = useState([]);

    const [selectedFields, setSelectedFields] = useState([
        'emp_code', 'name', 'department', 'designation', 'status'
    ]);
    const [visibleColumns, setVisibleColumns] = useState([]);
    const [filters, setFilters] = useState({
        department_id: '',
        unit_id: '',
        status: 'All',
        doj_from: '',
        doj_to: ''
    });
    const [showPreview, setShowPreview] = useState(false);
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const [searchField, setSearchField] = useState('');

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
    const previewRef = useRef(null);

    // Field categories with all fields
    const fieldCategories = React.useMemo(() => [
        { id: 'basic', label: 'Basic Information', color: 'primary', icon: 'bi-person' },
        { id: 'contact', label: 'Contact Details', color: 'info', icon: 'bi-telephone' },
        { id: 'employment', label: 'Employment Details', color: 'success', icon: 'bi-briefcase' },
        { id: 'salary', label: 'Salary Details', color: 'warning', icon: 'bi-cash' },
        { id: 'statutory', label: 'Statutory Details', color: 'danger', icon: 'bi-shield-check' },
        { id: 'attendance', label: 'Attendance Details', color: 'secondary', icon: 'bi-calendar-check' },
        { id: 'documents', label: 'Documents', color: 'purple', icon: 'bi-file-text' },
        { id: 'bank', label: 'Bank Details', color: 'dark', icon: 'bi-bank' },
        { id: 'company', label: 'Company Details', color: 'info', icon: 'bi-building' },
        { id: 'system', label: 'System Fields', color: 'secondary', icon: 'bi-gear' },
        { id: 'custom', label: 'Custom Fields', color: 'success', icon: 'bi-tags' }
    ], []);

    const defaultFieldOptions = React.useMemo(() => [
        // Basic Information
        { id: 'emp_id', label: 'Employee ID', category: 'basic' },
        { id: 'emp_code', label: 'Employee Code', category: 'basic' },
        { id: 'first_name', label: 'First Name', category: 'basic' },
        { id: 'last_name', label: 'Last Name', category: 'basic' },
        { id: 'name', label: 'Full Name', category: 'basic' },
        { id: 'gender', label: 'Gender', category: 'basic' },
        { id: 'dob', label: 'Date of Birth', category: 'basic' },
        { id: 'photo', label: 'Photo', category: 'basic' },

        // Contact Details
        { id: 'email', label: 'Email', category: 'contact' },
        { id: 'phone', label: 'Phone', category: 'contact' },

        // Employment Details
        { id: 'doj', label: 'Date of Joining', category: 'employment' },
        { id: 'dor', label: 'Date of Resignation', category: 'employment' },
        { id: 'status', label: 'Status', category: 'employment' },
        { id: 'department', label: 'Department', category: 'employment' },
        { id: 'designation', label: 'Designation', category: 'employment' },
        { id: 'unit', label: 'Unit', category: 'employment' },
        { id: 'weeklyoff_1', label: 'Weekly Off 1', category: 'employment' },
        { id: 'weeklyoff_2', label: 'Weekly Off 2', category: 'employment' },

        // Salary Details
        { id: 'salary_type', label: 'Salary Type', category: 'salary' },
        { id: 'salary_value', label: 'Salary Value', category: 'salary' },
        { id: 'has_salary_settings', label: 'Has Salary Settings', category: 'salary' },

        // Statutory Details
        { id: 'eligible_for_pf', label: 'Eligible for PF', category: 'statutory' },
        { id: 'pf_percentage', label: 'PF Percentage', category: 'statutory' },
        { id: 'pf_employer_percentage', label: 'PF Employer %', category: 'statutory' },
        { id: 'pf_ceiling', label: 'PF Ceiling', category: 'statutory' },
        { id: 'eligible_for_esi', label: 'Eligible for ESI', category: 'statutory' },
        { id: 'esi_percentage', label: 'ESI Percentage', category: 'statutory' },
        { id: 'esi_employer_percentage', label: 'ESI Employer %', category: 'statutory' },
        { id: 'esi_ceiling', label: 'ESI Ceiling', category: 'statutory' },
        { id: 'it_applicable', label: 'IT Applicable', category: 'statutory' },

        // Attendance Details
        { id: 'min_hour_for_present', label: 'Min Hours for Present', category: 'attendance' },
        { id: 'ot_hour_beyond_wh', label: 'OT Hours Beyond WH', category: 'attendance' },
        { id: 'present_days_type', label: 'Working Days Type', category: 'attendance' },
        { id: 'standard_days', label: 'Standard Days', category: 'attendance' },

        // Documents
        { id: 'pan_number', label: 'PAN Number', category: 'documents' },
        { id: 'aadhaar_number', label: 'Aadhaar Number', category: 'documents' },

        // Bank Details
        { id: 'bank_name', label: 'Bank Name', category: 'bank' },
        { id: 'bank_account', label: 'Bank Account', category: 'bank' },
        { id: 'ifsc_code', label: 'IFSC Code', category: 'bank' },

        // Company Details
        { id: 'company_name', label: 'Company Name', category: 'company' },
        { id: 'company_code', label: 'Company Code', category: 'company' },
        { id: 'company_address', label: 'Company Address', category: 'company' },
        { id: 'company_city', label: 'Company City', category: 'company' },
        { id: 'company_state', label: 'Company State', category: 'company' },
        { id: 'company_country', label: 'Company Country', category: 'company' },
        { id: 'company_phone', label: 'Company Phone', category: 'company' },
        { id: 'company_email', label: 'Company Email', category: 'company' },
        { id: 'gst_number', label: 'GST Number', category: 'company' },

        // System Fields
        { id: 'created_at', label: 'Created Date', category: 'system' },
        { id: 'updated_at', label: 'Updated Date', category: 'system' }
    ], []);

    const [fieldOptions, setFieldOptions] = useState(defaultFieldOptions);

    const fetchDepartments = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}/get_departments.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setDepartments(response.data.departments || []);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
        }
    }, [API_BASE]);

    const fetchUnits = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}/get_units.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setUnits(response.data.units || []);
            }
        } catch (error) {
            console.error('Error fetching units:', error);
        }
    }, [API_BASE]);

    const fetchCustomFields = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}employees/get_custom_fields.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                const fields = response.data.custom_fields || [];
                const customFieldsList = fields.map(field => ({
                    id: field.field_name,
                    label: field.field_name,
                    category: 'custom',
                    type: field.field_type,
                    options: field.field_options
                }));
                setCustomFields(customFieldsList);
                setFieldOptions(prev => {
                    // Filter out existing custom fields to avoid duplicates
                    const nonCustomFields = prev.filter(f => f.category !== 'custom');
                    return [...nonCustomFields, ...customFieldsList];
                });
            }
        } catch (error) {
            console.error('Error fetching custom fields:', error);
        }
    }, [API_BASE]);

    const fetchFieldMetadata = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}employees/export_employees.php`,
                { selectedFields: [], filters: {}, format: 'json' },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (response.data.success && response.data.field_metadata) {
                // Update field options with metadata
                const newFieldOptions = [...defaultFieldOptions];
                response.data.field_metadata.forEach(field => {
                    if (!newFieldOptions.some(f => f.id === field.id)) {
                        newFieldOptions.push({
                            id: field.id,
                            label: field.label,
                            category: field.category
                        });
                    }
                });
                setFieldOptions(newFieldOptions);
            }
        } catch (error) {
            console.error('Error fetching field metadata:', error);
        }
    }, [API_BASE, defaultFieldOptions]);

    const fetchPreview = async () => {
        setPreviewLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}employees/export_employees.php`,
                {
                    selectedFields: selectedFields,
                    filters: filters,
                    format: 'json'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setEmployeesData(response.data.employees || []);
                setShowPreview(true);

                // Initialize visible columns with all selected fields
                if (response.data.employees.length > 0) {
                    const firstEmployee = response.data.employees[0];
                    const availableColumns = Object.keys(firstEmployee);
                    setVisibleColumns(availableColumns);
                }

                // Scroll to preview
                setTimeout(() => {
                    if (previewRef.current) {
                        previewRef.current.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);
            } else {
                alert(`Error fetching preview: ${response.data.message || JSON.stringify(response.data)}`);
            }
        } catch (error) {
            console.error('Error fetching preview:', error);
            alert(`Error fetching preview: ${error.message}${error.response ? ' - ' + JSON.stringify(error.response.data) : ''}`);
        } finally {
            setPreviewLoading(false);
        }
    };

    useEffect(() => {
        fetchDepartments();
        fetchUnits();
        fetchCustomFields();
        fetchFieldMetadata();
    }, [fetchDepartments, fetchUnits, fetchCustomFields, fetchFieldMetadata]);

    const handleFieldSelection = (fieldId) => {
        setSelectedFields(prev =>
            prev.includes(fieldId)
                ? prev.filter(id => id !== fieldId)
                : [...prev, fieldId]
        );
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSelectAll = (category) => {
        const categoryFields = fieldOptions
            .filter(field => field.category === category)
            .map(field => field.id);

        const allSelected = categoryFields.every(field => selectedFields.includes(field));

        if (allSelected) {
            setSelectedFields(prev => prev.filter(field => !categoryFields.includes(field)));
        } else {
            setSelectedFields(prev => {
                const newFields = [...prev];
                categoryFields.forEach(field => {
                    if (!newFields.includes(field)) {
                        newFields.push(field);
                    }
                });
                return newFields;
            });
        }
    };

    const handleSelectAllFields = () => {
        const allFieldIds = fieldOptions.map(field => field.id);
        if (selectedFields.length === allFieldIds.length) {
            setSelectedFields([]);
        } else {
            setSelectedFields([...allFieldIds]);
        }
    };

    const toggleColumnVisibility = (columnName) => {
        setVisibleColumns(prev =>
            prev.includes(columnName)
                ? prev.filter(col => col !== columnName)
                : [...prev, columnName]
        );
    };

    const showAllColumns = () => {
        if (employeesData.length > 0) {
            const firstEmployee = employeesData[0];
            setVisibleColumns(Object.keys(firstEmployee));
        }
    };

    const hideAllColumns = () => {
        setVisibleColumns([]);
    };

    const getEmployeeValue = (emp, fieldId, label) => {
        if (!emp) return '';
        // Try direct ID match first
        if (emp[fieldId] !== undefined && emp[fieldId] !== null && String(emp[fieldId]).trim() !== '') return String(emp[fieldId]);
        // Try Label match
        if (emp[label] !== undefined && emp[label] !== null && String(emp[label]).trim() !== '') return String(emp[label]);

        // Try case-insensitive search through all object keys
        const lowerId = fieldId.toLowerCase();
        const lowerLabel = String(label || '').toLowerCase();
        const keys = Object.keys(emp);

        const foundKey = keys.find(k => k.toLowerCase() === lowerId || k.toLowerCase() === lowerLabel);
        if (foundKey) {
            const val = emp[foundKey];
            return val !== undefined && val !== null ? String(val) : '';
        }

        return '';
    };

    const exportToPDF = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}employees/export_employees.php`,
                {
                    selectedFields: selectedFields,
                    filters: filters,
                    format: 'json'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                const { company, employees } = response.data;

                // Create PDF
                const doc = new jsPDF({
                    orientation: 'landscape',
                    unit: 'mm',
                    format: 'a4'
                });

                // Company Header
                doc.setFontSize(16);
                doc.setTextColor(40, 40, 40);
                doc.text(company?.name || 'Employee Report', 14, 20);

                doc.setFontSize(10);
                doc.setTextColor(100, 100, 100);
                doc.text(`Employee Report - Generated on ${new Date().toLocaleDateString()}`, 14, 28);

                // Company Details
                if (company?.address) {
                    doc.text(`Address: ${company.address}, ${company.city || ''}, ${company.state || ''}`, 14, 36);
                }
                if (company?.phone) {
                    doc.text(`Phone: ${company.phone} | Email: ${company.email || ''}`, 14, 42);
                }

                // Filter Summary
                let filterText = 'Filters: ';
                const filterParts = [];
                if (filters.department_id) {
                    const dept = departments.find(d => String(d.dept_id) === String(filters.department_id));
                    if (dept) filterParts.push(`Dept: ${dept.dept_name}`);
                }
                if (filters.unit_id) {
                    const unit = units.find(u => String(u.unit_id) === String(filters.unit_id));
                    if (unit) filterParts.push(`Unit: ${unit.unit_name}`);
                }
                if (filters.status && filters.status !== 'All') {
                    filterParts.push(`Status: ${filters.status}`);
                }
                if (filters.doj_from || filters.doj_to) {
                    filterParts.push(`DOJ: ${filters.doj_from || ''} to ${filters.doj_to || ''}`);
                }

                if (filterParts.length > 0) {
                    filterText += filterParts.join(' | ');
                    doc.text(filterText, 14, 48);
                }

                // Prepare table data
                const headers = selectedFields.map(fieldId => {
                    const field = fieldOptions.find(f => f.id === fieldId);
                    return field ? field.label : fieldId;
                });

                const tableData = employees.map(emp =>
                    selectedFields.map(fieldId => {
                        const field = fieldOptions.find(f => f.id === fieldId);
                        const label = field ? field.label : fieldId;
                        return getEmployeeValue(emp, fieldId, label);
                    })
                );

                // Add table
                doc.autoTable({
                    head: [headers],
                    body: tableData,
                    startY: 55,
                    styles: {
                        fontSize: 8,
                        cellPadding: 2,
                        overflow: 'linebreak',
                        cellWidth: 'wrap'
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontStyle: 'bold'
                    },
                    alternateRowStyles: {
                        fillColor: [245, 245, 245]
                    },
                    margin: { top: 55 }
                });

                // Footer
                const pageCount = doc.internal.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.setFontSize(8);
                    doc.setTextColor(150, 150, 150);
                    doc.text(
                        `Page ${i} of ${pageCount} | Total Employees: ${response.data.total}`,
                        doc.internal.pageSize.width - 50,
                        doc.internal.pageSize.height - 10
                    );
                }

                // Save PDF
                doc.save(`employee_report_${new Date().toISOString().split('T')[0]}.pdf`);
            } else {
                alert(`Error fetching data for PDF: ${response.data.message || JSON.stringify(response.data)}`);
            }
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            alert(`Error generating PDF: ${error.message}${error.response ? ' - ' + JSON.stringify(error.response.data) : ''}`);
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}employees/export_employees.php`,
                {
                    selectedFields: selectedFields,
                    filters: filters,
                    format: 'json'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                const { company, employees } = response.data;

                // Prepare data for Excel
                const headers = selectedFields.map(fieldId => {
                    const field = fieldOptions.find(f => f.id === fieldId);
                    return field ? field.label : fieldId;
                });

                const excelData = [
                    // Company Info
                    [`Company: ${company.name}`],
                    [`Address: ${company.address || ''}`],
                    [`City: ${company.city || ''}, State: ${company.state || ''}`],
                    [`Phone: ${company.phone || ''}, Email: ${company.email || ''}`],
                    [''],
                    ['Employee Report'],
                    ['']
                ];

                // Add headers
                excelData.push(headers);

                // Add employee data
                employees.forEach(emp => {
                    const row = selectedFields.map(fieldId => {
                        const field = fieldOptions.find(f => f.id === fieldId);
                        const label = field ? field.label : fieldId;
                        return getEmployeeValue(emp, fieldId, label);
                    });
                    excelData.push(row);
                });

                // Create workbook
                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.aoa_to_sheet(excelData);

                // Style header row
                const range = XLSX.utils.decode_range(ws['!ref']);
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const address = XLSX.utils.encode_cell({ r: 7, c: C });
                    if (!ws[address]) continue;
                    ws[address].s = {
                        font: { bold: true, color: { rgb: "FFFFFF" } },
                        fill: { fgColor: { rgb: "2980B9" } },
                        alignment: { horizontal: "center" }
                    };
                }

                // Set column widths
                const colWidths = headers.map(() => ({ wch: 20 }));
                ws['!cols'] = colWidths;

                // Add worksheet to workbook
                XLSX.utils.book_append_sheet(wb, ws, 'Employee Report');

                // Save file
                XLSX.writeFile(wb, `employee_report_${new Date().toISOString().split('T')[0]}.xlsx`);
            } else {
                alert(`Error fetching data for Excel: ${response.data.message || JSON.stringify(response.data)}`);
            }
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            alert(`Error generating Excel: ${error.message}${error.response ? ' - ' + JSON.stringify(error.response.data) : ''}`);
        } finally {
            setLoading(false);
        }
    };

    const exportToCSV = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}employees/export_employees.php`,
                {
                    selectedFields: selectedFields,
                    filters: filters,
                    format: 'json'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                const { employees } = response.data;

                // Prepare headers
                const headers = selectedFields.map(fieldId => {
                    const field = fieldOptions.find(f => f.id === fieldId);
                    return field ? field.label : fieldId;
                });

                // Prepare CSV content
                let csvContent = headers.join(',') + '\n';

                employees.forEach(emp => {
                    const row = selectedFields.map(fieldId => {
                        const field = fieldOptions.find(f => f.id === fieldId);
                        const label = field ? field.label : fieldId;
                        let value = getEmployeeValue(emp, fieldId, label);
                        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                            value = '"' + value.replace(/"/g, '""') + '"';
                        }
                        return value;
                    });
                    csvContent += row.join(',') + '\n';
                });

                // Create and download CSV file
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `employee_report_${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                alert(`Error fetching data for CSV: ${response.data.message || JSON.stringify(response.data)}`);
            }
        } catch (error) {
            console.error('Error exporting to CSV:', error);
            alert(`Error generating CSV: ${error.message}${error.response ? ' - ' + JSON.stringify(error.response.data) : ''}`);
        } finally {
            setLoading(false);
        }
    };

    const printReport = async () => {
        const printWindow = window.open('', '_blank');
        setLoading(true);

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}employees/export_employees.php`,
                {
                    selectedFields: selectedFields,
                    filters: filters,
                    format: 'json'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                const { company, employees } = response.data;

                // Prepare headers
                const headers = selectedFields.map(fieldId => {
                    const field = fieldOptions.find(f => f.id === fieldId);
                    return field ? field.label : fieldId;
                });

                let printContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Employee Report</title>
            <style>
              @media print {
                body { font-family: Arial, sans-serif; margin: 20px; }
                .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
                .company-name { font-size: 24px; font-weight: bold; color: #2c3e50; }
                .report-title { font-size: 18px; color: #34495e; margin: 10px 0; }
                .company-info { font-size: 12px; color: #7f8c8d; margin-bottom: 15px; }
                .filters { font-size: 11px; color: #95a5a6; margin-bottom: 20px; }
                table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 9px; }
                th { background-color: #3498db; color: white; padding: 6px; text-align: left; }
                td { padding: 4px; border: 1px solid #ddd; }
                tr:nth-child(even) { background-color: #f8f9fa; }
                .footer { margin-top: 30px; text-align: center; font-size: 9px; color: #95a5a6; }
                @page { margin: 15mm; size: landscape; }
                .break-page { page-break-before: always; }
              }
              @media screen {
                body { font-family: Arial, sans-serif; margin: 40px; }
                .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
                .company-name { font-size: 28px; font-weight: bold; color: #2c3e50; }
                .report-title { font-size: 20px; color: #34495e; margin: 15px 0; }
                .company-info { font-size: 14px; color: #7f8c8d; margin-bottom: 20px; }
                .filters { font-size: 13px; color: #95a5a6; margin-bottom: 25px; }
                table { width: 100%; border-collapse: collapse; margin-top: 25px; font-size: 11px; }
                th { background-color: #3498db; color: white; padding: 8px; text-align: left; }
                td { padding: 6px; border: 1px solid #ddd; }
                tr:nth-child(even) { background-color: #f8f9fa; }
                .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #95a5a6; }
              }
              th, td { white-space: nowrap; }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="company-name">${company?.name || 'Company Name'}</div>
              <div class="report-title">Employee Report</div>
              <div class="company-info">
                ${company?.address ? `<div>${company.address}, ${company.city || ''}, ${company.state || ''}</div>` : ''}
                ${company?.phone ? `<div>Phone: ${company.phone} | Email: ${company.email || ''}</div>` : ''}
              </div>
              <div class="filters">
                Generated on: ${new Date().toLocaleDateString()} | 
                Total Employees: ${response.data.total} |
                Selected Fields: ${selectedFields.length}
              </div>
            </div>
            
            <table>
              <thead>
                <tr>
                  ${headers.map(header => `<th>${header}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${employees.map((emp, index) => `
                  <tr>
                    ${selectedFields.map(fieldId => {
                    const field = fieldOptions.find(f => f.id === fieldId);
                    const label = field ? field.label : fieldId;
                    const value = getEmployeeValue(emp, fieldId, label);
                    return `<td>${value}</td>`;
                }).join('')}
                  </tr>
                  ${(index + 1) % 40 === 0 ? '<tr class="break-page"></tr>' : ''}
                `).join('')}
              </tbody>
            </table>
            
            <div class="footer">
              <p>Report generated by HRMS | ${new Date().toLocaleString()} | Page 1</p>
            </div>
            
            <script>
              window.onload = function() {
                window.print();
                setTimeout(function() {
                  window.close();
                }, 500);
              }
            </script>
          </body>
          </html>
        `;

                if (printWindow) {
                    printWindow.document.write(printContent);
                    printWindow.document.close();
                }
            } else {
                alert(`Error fetching data for print: ${response.data.message || JSON.stringify(response.data)}`);
                if (printWindow) printWindow.close();
            }
        } catch (error) {
            console.error('Error generating print report:', error);
            alert(`Error generating print report: ${error.message}${error.response ? ' - ' + JSON.stringify(error.response.data) : ''}`);
            if (printWindow) printWindow.close();
        } finally {
            setLoading(false);
        }
    };

    const filteredFieldOptions = searchField
        ? fieldOptions.filter(field =>
            field.label.toLowerCase().includes(searchField.toLowerCase()) ||
            field.id.toLowerCase().includes(searchField.toLowerCase()) ||
            field.category.toLowerCase().includes(searchField.toLowerCase())
        )
        : fieldOptions;

    return (
        <div className="container-fluid py-4 employee-export-container">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <h2 className="h4 mb-1 fw-bold text-dark">
                                        <i className="bi bi-download me-2 text-primary"></i>
                                        Export Employee Details
                                    </h2>
                                    <p className="text-muted mb-0">
                                        Select fields, apply filters, preview data, and export in multiple formats
                                    </p>
                                    <small className="text-muted">
                                        Total Available Fields: {fieldOptions.length} | Selected: {selectedFields.length}
                                    </small>
                                </div>
                                <div className="col-md-6 text-end">
                                    <div className="btn-group">
                                        <button
                                            className="btn btn-outline-secondary me-2"
                                            onClick={() => window.history.back()}
                                        >
                                            <i className="bi bi-arrow-left me-2"></i>
                                            Back
                                        </button>
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={() => setShowColumnSelector(!showColumnSelector)}
                                        >
                                            <i className="bi bi-columns me-2"></i>
                                            {showColumnSelector ? 'Hide Field Selector' : 'Show Field Selector'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters Section */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom">
                            <h5 className="mb-0">
                                <i className="bi bi-funnel me-2 text-primary"></i>
                                Filters
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="form-label">Department</label>
                                    <select
                                        className="form-select"
                                        value={filters.department_id}
                                        onChange={(e) => handleFilterChange('department_id', e.target.value)}
                                    >
                                        <option value="">All Departments</option>
                                        {departments.map(dept => (
                                            <option key={dept.dept_id} value={dept.dept_id}>
                                                {dept.dept_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Unit</label>
                                    <select
                                        className="form-select"
                                        value={filters.unit_id}
                                        onChange={(e) => handleFilterChange('unit_id', e.target.value)}
                                    >
                                        <option value="">All Units</option>
                                        {units.map(unit => (
                                            <option key={unit.unit_id} value={unit.unit_id}>
                                                {unit.unit_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Status</label>
                                    <select
                                        className="form-select"
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                    >
                                        <option value="All">All Status</option>
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                        <option value="Resigned">Resigned</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">DOJ From</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.doj_from}
                                        onChange={(e) => handleFilterChange('doj_from', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">DOJ To</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.doj_to}
                                        onChange={(e) => handleFilterChange('doj_to', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Field Selection Panel */}
            {showColumnSelector && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-bottom">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">
                                        <i className="bi bi-check2-square me-2 text-primary"></i>
                                        Select Fields to Export
                                    </h5>
                                    <div className="d-flex align-items-center">
                                        <div className="input-group me-3" style={{ width: '300px' }}>
                                            <span className="input-group-text bg-light">
                                                <i className="bi bi-search"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search fields..."
                                                value={searchField}
                                                onChange={(e) => setSearchField(e.target.value)}
                                            />
                                            {searchField && (
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => setSearchField('')}
                                                >
                                                    <i className="bi bi-x"></i>
                                                </button>
                                            )}
                                        </div>
                                        <button
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={handleSelectAllFields}
                                        >
                                            {selectedFields.length === fieldOptions.length ? 'Deselect All' : 'Select All'}
                                        </button>
                                    </div>
                                </div>
                                <small className="text-muted">
                                    Selected: {selectedFields.length} of {fieldOptions.length} fields
                                </small>
                            </div>
                            <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <div className="row">
                                    {fieldCategories.map(category => {
                                        const categoryFields = filteredFieldOptions.filter(f => f.category === category.id);
                                        if (categoryFields.length === 0) return null;

                                        const allSelected = categoryFields.every(f => selectedFields.includes(f.id));
                                        const selectedCount = categoryFields.filter(f => selectedFields.includes(f.id)).length;

                                        return (
                                            <div key={category.id} className="col-md-6 mb-4">
                                                <div className="card border h-100">
                                                    <div className={`card-header bg-${category.color} bg-opacity-10 border-${category.color} border-bottom`}>
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <h6 className="mb-0">
                                                                    <i className={`bi ${category.icon} me-2 text-${category.color}`}></i>
                                                                    {category.label}
                                                                    <span className="badge bg-light text-dark ms-2">
                                                                        {selectedCount}/{categoryFields.length}
                                                                    </span>
                                                                </h6>
                                                            </div>
                                                            <button
                                                                className={`btn btn-sm btn-outline-${category.color}`}
                                                                onClick={() => handleSelectAll(category.id)}
                                                            >
                                                                {allSelected ? 'Deselect All' : 'Select All'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="card-body p-0">
                                                        <div className="list-group list-group-flush">
                                                            {categoryFields.map(field => (
                                                                <label
                                                                    key={field.id}
                                                                    className="list-group-item list-group-item-action d-flex align-items-center py-2 px-3"
                                                                    style={{ cursor: 'pointer' }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 123, 255, 0.05)'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input me-3"
                                                                        checked={selectedFields.includes(field.id)}
                                                                        onChange={() => handleFieldSelection(field.id)}
                                                                    />
                                                                    <div className="flex-grow-1">
                                                                        <div className="fw-semibold">{field.label}</div>
                                                                        <small className="text-muted">{field.id}</small>
                                                                    </div>
                                                                    <span className={`badge bg-${category.color} bg-opacity-25 text-${category.color}`}>
                                                                        {field.category}
                                                                    </span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0">
                        <div className="card-body">
                            <div className="d-flex flex-wrap gap-2">
                                <button
                                    className="btn btn-primary"
                                    onClick={fetchPreview}
                                    disabled={previewLoading || selectedFields.length === 0}
                                >
                                    {previewLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Loading Preview...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-eye me-2"></i>
                                            Preview Data ({selectedFields.length} fields)
                                        </>
                                    )}
                                </button>

                                <button
                                    className="btn btn-danger"
                                    onClick={exportToPDF}
                                    disabled={loading || selectedFields.length === 0}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-file-pdf me-2"></i>
                                            Export PDF
                                        </>
                                    )}
                                </button>

                                <button
                                    className="btn btn-success"
                                    onClick={exportToExcel}
                                    disabled={loading || selectedFields.length === 0}
                                >
                                    <i className="bi bi-file-excel me-2"></i>
                                    Export Excel
                                </button>

                                <button
                                    className="btn btn-info"
                                    onClick={exportToCSV}
                                    disabled={loading || selectedFields.length === 0}
                                >
                                    <i className="bi bi-file-text me-2"></i>
                                    Export CSV
                                </button>

                                <button
                                    className="btn btn-warning"
                                    onClick={printReport}
                                    disabled={loading || selectedFields.length === 0}
                                >
                                    <i className="bi bi-printer me-2"></i>
                                    Print
                                </button>

                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => {
                                        setSelectedFields(['emp_code', 'name', 'department', 'designation', 'status']);
                                        setFilters({
                                            department_id: '',
                                            unit_id: '',
                                            status: 'All',
                                            doj_from: '',
                                            doj_to: ''
                                        });
                                        setShowPreview(false);
                                        setEmployeesData([]);
                                    }}
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Preview Section */}
            {showPreview && employeesData.length > 0 && (
                <div className="row" ref={previewRef}>
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-bottom">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">
                                        <i className="bi bi-table me-2 text-primary"></i>
                                        Data Preview
                                        <span className="badge bg-primary ms-2">
                                            {employeesData.length} Employees
                                        </span>
                                        <span className="badge bg-secondary ms-2">
                                            {visibleColumns.length} Visible Columns
                                        </span>
                                    </h5>
                                    <div className="btn-group">
                                        <button
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={showAllColumns}
                                        >
                                            <i className="bi bi-eye me-1"></i>
                                            Show All
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={hideAllColumns}
                                        >
                                            <i className="bi bi-eye-slash me-1"></i>
                                            Hide All
                                        </button>
                                        <button
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => setShowPreview(false)}
                                        >
                                            <i className="bi bi-x-lg me-1"></i>
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body p-0">
                                {/* Column Toggle Bar */}
                                <div className="bg-light p-3 border-bottom">
                                    <div className="d-flex flex-wrap gap-2 align-items-center">
                                        <small className="text-muted me-2">Toggle Columns:</small>
                                        {employeesData.length > 0 && Object.keys(employeesData[0]).map(column => (
                                            <div key={column} className="form-check form-check-inline">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`col-${column}`}
                                                    checked={visibleColumns.includes(column)}
                                                    onChange={() => toggleColumnVisibility(column)}
                                                />
                                                <label className="form-check-label" htmlFor={`col-${column}`}>
                                                    {column}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Data Table */}
                                <div className="table-responsive" style={{ maxHeight: '500px', overflow: 'auto' }}>
                                    <table className="table table-hover table-striped mb-0">
                                        <thead className="bg-light sticky-top">
                                            <tr>
                                                {employeesData.length > 0 &&
                                                    Object.keys(employeesData[0])
                                                        .filter(column => visibleColumns.includes(column))
                                                        .map(column => (
                                                            <th key={column} className="text-nowrap">
                                                                <div className="d-flex align-items-center">
                                                                    {column}
                                                                    <button
                                                                        className="btn btn-sm btn-link p-0 ms-1"
                                                                        onClick={() => toggleColumnVisibility(column)}
                                                                        title="Hide column"
                                                                    >
                                                                        <i className="bi bi-eye-slash text-muted"></i>
                                                                    </button>
                                                                </div>
                                                            </th>
                                                        ))
                                                }
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {employeesData.map((employee, index) => (
                                                <tr key={index}>
                                                    {Object.entries(employee)
                                                        .filter(([column]) => visibleColumns.includes(column))
                                                        .map(([column, value]) => (
                                                            <td key={column} className="text-nowrap">
                                                                {value !== null && value !== undefined ? (
                                                                    typeof value === 'string' && value.length > 50 ?
                                                                        <span title={value}>{value.substring(0, 50)}...</span> :
                                                                        String(value)
                                                                ) : ''}
                                                            </td>
                                                        ))
                                                    }
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Summary Footer */}
                                <div className="card-footer bg-light">
                                    <div className="row">
                                        <div className="col-md-3">
                                            <small className="text-muted">Total Records:</small>
                                            <div className="fw-bold">{employeesData.length}</div>
                                        </div>
                                        <div className="col-md-3">
                                            <small className="text-muted">Visible Columns:</small>
                                            <div className="fw-bold">{visibleColumns.length}</div>
                                        </div>
                                        <div className="col-md-3">
                                            <small className="text-muted">Selected Fields:</small>
                                            <div className="fw-bold">{selectedFields.length}</div>
                                        </div>
                                        <div className="col-md-3">
                                            <small className="text-muted">Total Fields Available:</small>
                                            <div className="fw-bold">{fieldOptions.length}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Stats */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <div className="fw-bold text-primary fs-4">{fieldOptions.length}</div>
                                    <small className="text-muted">Total Fields</small>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <div className="fw-bold text-success fs-4">{selectedFields.length}</div>
                                    <small className="text-muted">Selected Fields</small>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <div className="fw-bold text-info fs-4">{customFields.length}</div>
                                    <small className="text-muted">Custom Fields</small>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <div className="fw-bold text-warning fs-4">{departments.length}</div>
                                    <small className="text-muted">Departments</small>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <div className="fw-bold text-danger fs-4">{units.length}</div>
                                    <small className="text-muted">Units</small>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <div className="fw-bold text-dark fs-4">{employeesData.length}</div>
                                    <small className="text-muted">Preview Records</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .employee-export-container .form-check-input:checked {
                    background-color: var(--bs-primary);
                    border-color: var(--bs-primary);
                }
                
                .employee-export-container .card {
                    border-radius: 10px;
                }
                
                .employee-export-container .list-group-item:hover {
                    background-color: rgba(0, 123, 255, 0.05);
                }
                
                .employee-export-container .table th {
                    position: sticky;
                    top: 0;
                    background-color: #f8f9fa;
                    z-index: 10;
                }
                
                .employee-export-container .table-responsive::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                
                .employee-export-container .table-responsive::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 4px;
                }
                
                .employee-export-container .table-responsive::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 4px;
                }
                
                .employee-export-container .table-responsive::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }
                
                .sticky-top {
                    position: -webkit-sticky;
                    position: sticky;
                    top: 0;
                    z-index: 1020;
                }
            `}</style>
        </div>
    );
};

export default EmployeeExport;